import dayjs from 'dayjs';
import { GetListParams } from 'react-admin';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import { CustomDataProvider } from '../../../providers/CustomDataProvider';
import { Resources } from '../../resource';

// API stores `day` as UTC date string (YYYY-MM-DD = UTC midnight).
// Convert to/from the user's local timezone.
const utcDayToLocal = (utcDay: string): string => {
  if (!utcDay) return '';
  const parsed = dayjs.utc(utcDay);
  if (!parsed.isValid()) return utcDay;
  return parsed.local().format('YYYY-MM-DD');
};

export const localDayToUtc = (localDay: string): string => {
  if (!localDay) return '';
  const parsed = dayjs(localDay);
  if (!parsed.isValid()) return localDay;
  return parsed.utc().format('YYYY-MM-DD');
};

const flattenGroups = (json: any): any[] | null => {
  const groups = json?.groups ?? json?.data?.groups;
  if (!Array.isArray(groups)) return null;
  const result: any[] = [];
  groups.forEach((group: any) => {
    if (!Array.isArray(group?.items)) return;
    const localDay = utcDayToLocal(group.day);
    group.items.forEach((it: any) => {
      result.push({ ...it, day: localDay });
    });
  });
  return result;
};

const extractItems = (json: any) => {
  const grouped = flattenGroups(json);
  if (grouped) return grouped;
  const rows = json?.items ?? json?.data?.items ?? json?.data ?? json ?? [];
  return Array.isArray(rows) ? rows : [];
};

const normalizeListItem = (item: any): any => {
  if (!item?.payload || (item.type !== 'EVENT' && item.type !== 'MARKET')) {
    return item;
  }
  const { payload, type, day } = item;
  if (type === 'EVENT') {
    return {
      ...payload,
      type,
      day,
      question: payload.title || '',
    };
  }
  return { ...payload, type, day };
};

const toAcceptLanguage = (locale: string) => (locale === 'cn' ? 'zh' : locale);

export class WorldCupBreakingEventsDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    const { filter = {}, meta } = params;
    const search = new URLSearchParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        search.append(key, value);
      }
    });

    const headers = new Headers();
    if (typeof meta?.acceptLanguage === 'string' && meta.acceptLanguage.trim().length > 0) {
      headers.set('Accept-Language', toAcceptLanguage(meta.acceptLanguage));
    }

    const queryString = search.toString();
    const url = `${baseUrl}/${resource}${queryString ? `?${queryString}` : ''}`;
    const { json } = await httpClient(url, { method: 'GET', headers });
    const list = extractItems(json).map(normalizeListItem);

    return {
      data: list,
      total: json?.count ?? json?.data?.count ?? list.length,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: any): Promise<any> {
    const { id, meta } = params;
    const headers = new Headers();
    if (typeof meta?.acceptLanguage === 'string' && meta.acceptLanguage.trim().length > 0) {
      headers.set('Accept-Language', toAcceptLanguage(meta.acceptLanguage));
    }
    const { json } = await httpClient(`${baseUrl}/${resource}/${id}`, { method: 'GET', headers });
    const data = json?.data ?? json;
    return { data: { ...data, id: data?.id ?? id } };
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    if (resource !== Resources.worldCupBreakingEvents) {
      return Promise.reject(new Error('Unsupported create resource'));
    }

    const raw =
      params?.data && typeof params.data === 'object' && !Array.isArray(params.data)
        ? params.data
        : { items: [] };

    // Convert each item's local `day` back to UTC before sending to the server.
    const payload = {
      ...raw,
      items: Array.isArray(raw.items)
        ? raw.items.map((item: any) =>
            item?.day ? { ...item, day: localDayToUtc(item.day) } : item
          )
        : [],
    };

    const { json } = await httpClient(`${baseUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      data: {
        id: 'world-cup-breaking-news',
        items: json?.data ?? json ?? payload,
      },
    };
  }
}

export const worldCupBreakingEventsDataProvider = new WorldCupBreakingEventsDataProvider();
